import { useTranslation } from 'react-i18next';
import React from 'react';
import { Typography } from 'app/components/ui';
import { LabeledSection } from './LabeledSection';

interface TagContentBlock {
  tagId: string;
  value: string;
  list?: { ordered: boolean; items: string[] };
  location: { section: string; page: number };
  unplaced?: boolean;
}

const TagContentValue = ({
  value,
  list,
  isActive,
  muted,
  onSelect,
  action,
}: {
  value: string;
  list?: { ordered: boolean; items: string[] };
  isActive?: boolean;
  muted?: boolean;
  onSelect?: () => void;
  action?: React.ReactNode;
}) => {
  const { t } = useTranslation('tag');

  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);
  const [overflowing, setOverflowing] = React.useState(false);

  const listWithItems = list && list.items.length > 0 ? list : undefined;
  const isList = !!listWithItems;

  React.useLayoutEffect(() => {
    if (open) return; // only the collapsed view is clamped/measured
    const p = ref.current?.firstElementChild as HTMLElement | null;
    if (!p) return;
    setOverflowing(p.scrollHeight > p.clientHeight + 1);
  }, [value, isList, open]);

  // Toggle appears when there's more to reveal: a multi-item list, or text that
  // overflows the single clamped line.
  const hasMore = listWithItems ? listWithItems.items.length > 1 || overflowing : overflowing;

  const clampStyle: React.CSSProperties = open
    ? { whiteSpace: 'normal' }
    : { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

  const ListTag = list?.ordered ? 'ol' : 'ul';

  return (
    <div
      className={`show-more-container tag-content-block ${onSelect ? 'tag-content-block--selectable' : ''}`}
      style={isActive ? { borderLeft: '3px solid #547ba2', backgroundColor: '#eaf0f7' } : undefined}
      onClick={onSelect}
    >
      <div className="tag-content-block__body">
        {listWithItems ? (
          // Always a real <ol>/<ul> so the marker (1. / •) shows. Collapsed = first item
          // only with the first line clamped; expanded = every item.
          <ListTag style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {(open ? listWithItems.items : listWithItems.items.slice(0, 1)).map((item, i) => (
              <li key={i} ref={!open && i === 0 ? (ref as React.RefObject<HTMLLIElement>) : undefined}>
                <Typography variant="body-5" fontWeight={isActive ? 'normal' : 'light'} style={!open && i === 0 ? clampStyle : undefined}>
                  {item}
                </Typography>
              </li>
            ))}
          </ListTag>
        ) : (
          <div ref={ref as React.RefObject<HTMLDivElement>}>
            <Typography
              variant="body-5"
              fontWeight={isActive ? 'normal' : 'light'}
              color={muted ? '#8e8c94' : undefined}
              style={muted ? { ...clampStyle, fontStyle: 'italic' } : clampStyle}
            >
              {value}
            </Typography>
          </div>
        )}
        {(hasMore || open) && (
          <button
            type="button"
            className="show-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
            style={{ marginTop: 4 }}
          >
            <Typography variant="body-3" fontWeight="semibold" color="#547ba2">
              {open ? t('content.hide.textblock') : t('content.show.textblock')}
            </Typography>
          </button>
        )}
      </div>
      {action}
    </div>
  );
};

interface TagContentBlocksProps {
  taggedValue: TagContentBlock[];
  activeTagId?: string;
  onSelectBlock?: (tagId: string) => void;
  canSelectBlock?: (tagId: string) => boolean;
  onDeleteBlock?: (tagId: string) => void;
  onDeleteAll?: () => void;
}

export const TagContentBlocks = ({ taggedValue, activeTagId, onSelectBlock, canSelectBlock, onDeleteBlock, onDeleteAll }: TagContentBlocksProps) => {
  const { t } = useTranslation(['all', 'tag']);

  const [expanded, setExpanded] = React.useState(false);
  const blockCount = taggedValue.length;

  if (blockCount === 0) return null;

  const visibleBlocks = expanded ? taggedValue : [taggedValue.find((block) => !block.unplaced) ?? taggedValue[0]];

  return (
    <LabeledSection label={t('tag:content.title')}>
      <div className="show-more-wrapper">
        {visibleBlocks.map((block) => {
          const isActive = block.tagId === activeTagId;
          return (
            <div key={block.tagId}>
              {visibleBlocks.length > 1 && !block.unplaced && (
                <Typography variant="body-3" color="#717077" style={{ marginBottom: 4 }}>
                  {`${t('all:tags.page')} ${block.location.page}`}
                </Typography>
              )}
              <TagContentValue
                value={block.unplaced ? t('tag:content.chain.unplaced') : block.value}
                muted={block.unplaced}
                list={block.list}
                isActive={isActive}
                onSelect={
                  onSelectBlock && !isActive && !block.unplaced && (canSelectBlock?.(block.tagId) ?? true) ? () => onSelectBlock(block.tagId) : undefined
                }
                action={
                  onDeleteBlock && expanded && blockCount > 1 ? (
                    <button
                      type="button"
                      className="tag-content-block__delete"
                      title={t('tag:content.delete.block')}
                      aria-label={t('tag:content.delete.block')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteBlock(block.tagId);
                      }}
                    >
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  ) : undefined
                }
              />
            </div>
          );
        })}

        {blockCount > 1 && (
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <button type="button" className="show-more-btn" onClick={() => setExpanded((prev) => !prev)}>
              <Typography variant="body-3" fontWeight="semibold" color="#547ba2">
                {expanded ? t('tag:content.hide.blocks') : t('tag:content.show.blocks')}
              </Typography>
              <i className={`fa fa-chevron-down fa-sm ${expanded ? 'show-more' : ''}`} />
            </button>
            <Typography variant="body-3" fontWeight="light" color="#717077">
              {`${t('tag:content.includes')} ${blockCount} ${t('tag:content.text.blocks')}`}
            </Typography>
            {onDeleteAll && (
              <button type="button" className="tag-content-blocks__delete-all" onClick={onDeleteAll}>
                <i className="fa-regular fa-trash-can" />
                {t('tag:content.delete.all')}
              </button>
            )}
          </div>
        )}
      </div>
    </LabeledSection>
  );
};
